import { useEffect, useState } from "react";

/**
 * Custom hook to manage hot tip minimum price settings
 * @returns {Object} - Object containing hotTipMinPrice state and update function
 */
export const useHotTipSettings = () => {
  // Get the initial value synchronously to avoid flashing of default value
  const getInitialValue = () => {
    try {
      const savedValue = localStorage.getItem("hotTipMinPrice");
      return savedValue ? parseFloat(savedValue) : 0; // Default to 0 to disable by default
    } catch (e) {
      return 0; // Default to 0 if localStorage is not available
    }
  };

  const [hotTipMinPrice, setHotTipMinPrice] = useState(getInitialValue());
  const [isLoading, setIsLoading] = useState(true);

  // Initialize from localStorage on mount (just to ensure we have the correct value)
  useEffect(() => {
    try {
      const savedPreference = localStorage.getItem("hotTipMinPrice");
      if (savedPreference !== null) {
        setHotTipMinPrice(parseFloat(savedPreference));
      } else {
        // If no saved preference, initialize with default of 0
        localStorage.setItem("hotTipMinPrice", "0");
      }
    } catch (e) {
      console.error("Error reading from localStorage:", e);
    }
    setIsLoading(false);
  }, []);

  // Set up event listener for changes from other components
  useEffect(() => {
    const handleHotTipSettingsChange = (e) => {
      const updatedPreference = e?.detail?.value || localStorage.getItem("hotTipMinPrice");
      if (updatedPreference !== null) {
        setHotTipMinPrice(parseFloat(updatedPreference));
      }
    };

    window.addEventListener("hotTipSettingsChanged", handleHotTipSettingsChange);
    window.addEventListener("storage", (e) => {
      if (e.key === "hotTipMinPrice") {
        handleHotTipSettingsChange();
      }
    });

    return () => {
      window.removeEventListener("hotTipSettingsChanged", handleHotTipSettingsChange);
      window.removeEventListener("storage", handleHotTipSettingsChange);
    };
  }, []);

  // Function to update hot tip minimum price preference
  const updateHotTipMinPrice = (value) => {
    // Ensure value is within bounds, allow 0 to disable feature
    const validValue = Math.min(Math.max(parseFloat(value) || 0, 0), 1000);
    
    setHotTipMinPrice(validValue);
    localStorage.setItem("hotTipMinPrice", validValue.toString());

    // Dispatch a detailed event so other components can react to this change
    // Include the new value in the event detail to avoid race conditions
    window.dispatchEvent(new CustomEvent("hotTipSettingsChanged", {
      detail: { value: validValue }
    }));
    
    // Also trigger a manual refresh of relevant React Query data
    if (window.refreshTipsData) {
      window.refreshTipsData();
    }
  };

  return {
    hotTipMinPrice,
    updateHotTipMinPrice,
    isLoading
  };
}; 